import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, GlobeAltIcon } from "@heroicons/react/24/outline";

const languages = [
  { value: "kk", label: "Қазақша" },
  { value: "ru", label: "Русский" },
  { value: "en", label: "English" },
];

const variants = {
  // A bordered control for page corners (login, exam pages); the list opens below it.
  field: {
    button:
      "gap-2 rounded-xl bg-white px-3 py-2 text-slate-700 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300 ui-open:ring-slate-300",
    icon: "h-4 w-4",
    enterFrom: "-translate-y-1",
    options: "right-0 top-full mt-2 origin-top-right",
  },
  // Looks like a sidebar menu item; the list opens above it (it sits at the bottom of the sidebar).
  nav: {
    button: "gap-3 rounded-xl px-3 py-2.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 ui-open:bg-slate-100 ui-open:text-slate-900",
    icon: "h-5 w-5",
    enterFrom: "translate-y-1",
    options: "bottom-full left-0 mb-1 origin-bottom",
  },
};

// Language picker; i18next remembers the choice in localStorage.
export default function LanguageDropdown({ variant = "field", className = "" }) {
  const { t, i18n } = useTranslation();
  // A detected browser locale such as "en-US" resolves to "en"; match on that.
  const current = languages.find((l) => l.value === i18n.resolvedLanguage) || languages[0];
  const styles = variants[variant];

  return (
    <Listbox value={current.value} onChange={(lng) => i18n.changeLanguage(lng)}>
      <div className={`relative ${className}`}>
        <Listbox.Button
          aria-label={t("language.label")}
          className={`flex w-full items-center text-sm font-medium transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${styles.button}`}
        >
          <GlobeAltIcon className={`shrink-0 text-slate-400 ${styles.icon}`} aria-hidden="true" />
          <span className="flex-1 truncate text-left">{current.label}</span>
          <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        </Listbox.Button>
        <Transition
          as={Fragment}
          enter="transition duration-150 ease-out"
          enterFrom={`opacity-0 scale-95 ${styles.enterFrom}`}
          enterTo="opacity-100 scale-100 translate-y-0"
          leave="transition duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Listbox.Options
            className={`absolute z-50 min-w-full rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-900/5 focus:outline-none ${styles.options}`}
          >
            {languages.map((lang) => (
              <Listbox.Option
                key={lang.value}
                value={lang.value}
                className={({ active }) =>
                  `flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${
                    active ? "bg-slate-100 text-slate-900" : "text-slate-700"
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span className={`flex-1 ${selected ? "font-semibold text-blue-700" : ""}`}>{lang.label}</span>
                    <CheckIcon
                      className={`h-4 w-4 text-blue-600 ${selected ? "" : "invisible"}`}
                      aria-hidden="true"
                    />
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
